/**
 * 大六壬 · 领域常量表
 *
 * 本文件只放「死数据 + 纯查表函数」，不含任何推演逻辑（推演在 engine.ts / tianJiang.ts 等）。
 * 所有索引约定统一为 **0 基**：
 * - 天干索引 0..9  = 甲…癸
 * - 地支索引 0..11 = 子…亥（与地支序号自洽，可直接做模 12 运算）
 *
 * 依据：十二地支五行阴阳、十干寄宫、十二月将（中气换将）、贵人诀、十二天将序、
 * 旬空、长生十二宫、神煞取法均取六壬通行版本，个别有异说的表在注释中标注。
 */
// ════════════════════════════════════════════════════════════
// 一、天干 / 地支基础
// ════════════════════════════════════════════════════════════
/** 十天干 */
export const GAN = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
/** 十二地支 */
export const ZHI = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
/** 天干索引（0..9），非法返回 -1 */
export function ganIndex(gan) {
    return GAN.indexOf(gan);
}
/** 地支索引（0..11），非法返回 -1 */
export function zhiIndex(zhi) {
    return ZHI.indexOf(zhi);
}
/** 地支索引取支名（自动取模） */
export function zhiOf(index) {
    return ZHI[((index % 12) + 12) % 12];
}
/** 天干索引取干名（自动取模） */
export function ganOf(index) {
    return GAN[((index % 10) + 10) % 10];
}
/** 是否阳干（甲丙戊庚壬） */
export function isYangGan(index) {
    return index % 2 === 0;
}
/** 是否阳支（子寅辰午申戌） */
export function isYangZhi(index) {
    return index % 2 === 0;
}
/** 五行名（按索引） */
export const WU_XING = ['木', '火', '土', '金', '水'];
/** 天干五行索引：甲乙木、丙丁火、戊己土、庚辛金、壬癸水 */
export function ganWuXing(ganIdx) {
    return Math.floor(ganIdx / 2);
}
/** 地支五行索引：子水 丑土 寅木 卯木 辰土 巳火 午火 未土 申金 酉金 戌土 亥水 */
const ZHI_WU_XING = [4, 2, 0, 0, 2, 1, 1, 2, 3, 3, 2, 4];
/** 地支五行索引 */
export function zhiWuXing(zhiIdx) {
    return ZHI_WU_XING[((zhiIdx % 12) + 12) % 12];
}
/** a 是否克 b（五行相克：木克土、土克水、水克火、火克金、金克木） */
export function ke(a, b) {
    return (a + 2) % 5 === b;
}
/** a 是否生 b（五行相生：木生火、火生土、土生金、金生水、水生木） */
export function sheng(a, b) {
    return (a + 1) % 5 === b;
}
// ════════════════════════════════════════════════════════════
// 三、十干寄宫（甲寅、乙辰、丙巳、丁未、戊巳、己未、庚申、辛戌、壬亥、癸丑）
// ════════════════════════════════════════════════════════════
/** 日干 → 寄宫地支索引 */
const GAN_JI_GONG = [2, 4, 5, 7, 5, 7, 8, 10, 11, 1];
/** 取某天干的寄宫地支索引 */
export function jiGong(ganIdx) {
    return GAN_JI_GONG[((ganIdx % 10) + 10) % 10];
}
/**
 * 中气 → 月将（太阳过宫）。
 * 大寒→子神后、雨水→亥登明、春分→戌河魁、谷雨→酉从魁、小满→申传送、夏至→未小吉、
 * 大暑→午胜光、处暑→巳太乙、秋分→辰天罡、霜降→卯太冲、小雪→寅功曹、冬至→丑大吉。
 */
export const YUE_JIANG_TABLE = [
    { qi: '大寒', zhi: '子', name: '神后' },
    { qi: '雨水', zhi: '亥', name: '登明' },
    { qi: '春分', zhi: '戌', name: '河魁' },
    { qi: '谷雨', zhi: '酉', name: '从魁' },
    { qi: '小满', zhi: '申', name: '传送' },
    { qi: '夏至', zhi: '未', name: '小吉' },
    { qi: '大暑', zhi: '午', name: '胜光' },
    { qi: '处暑', zhi: '巳', name: '太乙' },
    { qi: '秋分', zhi: '辰', name: '天罡' },
    { qi: '霜降', zhi: '卯', name: '太冲' },
    { qi: '小雪', zhi: '寅', name: '功曹' },
    { qi: '冬至', zhi: '丑', name: '大吉' },
];
/** 十二中气（按公历年内先后排列，用于定位「当前所处中气区间」） */
export const ZHONG_QI = [
    '大寒',
    '雨水',
    '春分',
    '谷雨',
    '小满',
    '夏至',
    '大暑',
    '处暑',
    '秋分',
    '霜降',
    '小雪',
    '冬至',
];
const YUE_JIANG_BY_QI = new Map(YUE_JIANG_TABLE.map((y) => [y.qi, y]));
/** 按中气名取月将 */
export function yueJiangByQi(qi) {
    return YUE_JIANG_BY_QI.get(qi) ?? null;
}
/** 按地支取月将信息 */
export function yueJiangByZhi(zhi) {
    return YUE_JIANG_TABLE.find((y) => y.zhi === zhi) ?? null;
}
// ════════════════════════════════════════════════════════════
// 五、十二天将 + 贵人诀
// ════════════════════════════════════════════════════════════
/**
 * 十二天将（顺布次序）：贵人 → 螣蛇 → 朱雀 → 六合 → 勾陈 → 青龙 →
 * 天空 → 白虎 → 太常 → 玄武 → 太阴 → 天后
 */
export const TIAN_JIANG = [
    '贵人',
    '螣蛇',
    '朱雀',
    '六合',
    '勾陈',
    '青龙',
    '天空',
    '白虎',
    '太常',
    '玄武',
    '太阴',
    '天后',
];
/** 天将吉凶倾向（供断课规则引擎使用，中性为 0） */
export const TIAN_JIANG_LUCK = {
    贵人: 2,
    螣蛇: -2,
    朱雀: -1,
    六合: 1,
    勾陈: -1,
    青龙: 2,
    天空: -2,
    白虎: -2,
    太常: 1,
    玄武: -2,
    太阴: 1,
    天后: 1,
};
/** 天将一句话释义 */
export const TIAN_JIANG_DESC = {
    贵人: '尊贵扶助、得人提拔，主贵人相助、事情有位',
    螣蛇: '虚惊怪异、缠绕不安，主虚诈惊恐、缠绵难解',
    朱雀: '文书口舌、信息传递，主文书信息、亦主是非争讼',
    六合: '和合成就、媒妁牵合，主和合、交易、婚姻、合作',
    勾陈: '勾连迟滞、田土争讼，主纠缠拖延、田产讼事',
    青龙: '喜庆财帛、进财升迁，主喜庆、财利、官职',
    天空: '虚妄落空、欺诳不实，主虚耗、落空、不实之言',
    白虎: '凶伤疾病、道路血腥，主伤病、丧服、急迫之事',
    太常: '衣食宴乐、平常和顺，主衣食、宴会、礼仪、平安',
    玄武: '盗贼暗昧、阴私逃亡，主失脱、盗贼、暗昧不明',
    太阴: '阴私暗助、女子之事，主暗中相助、谋划、女眷',
    天后: '妇女恩泽、婚姻孕育，主妇女、婚姻、恩泽、臣妾',
};
/**
 * 贵人诀：甲戊庚牛羊、乙己鼠猴乡、丙丁猪鸡位、壬癸兔蛇藏、辛逢马虎。
 * 表结构 [昼贵地支索引, 夜贵地支索引]。
 */
const GUI_REN_BY_GAN = [
    [1, 7], // 甲：昼丑(1) / 夜未(7)
    [0, 8], // 乙：昼子(0) / 夜申(8)
    [11, 9], // 丙：昼亥(11) / 夜酉(9)
    [11, 9], // 丁：昼亥 / 夜酉
    [1, 7], // 戊：昼丑 / 夜未
    [0, 8], // 己：昼子 / 夜申
    [1, 7], // 庚：昼丑 / 夜未
    [6, 2], // 辛：昼午(6) 马 / 夜寅(2) 虎
    [3, 5], // 壬：昼卯(3) 兔 / 夜巳(5) 蛇
    [3, 5], // 癸：昼卯 / 夜巳
];
/**
 * 取某日干的昼夜贵人地支索引。
 * @param ganIdx 日干索引 0..9
 * @returns [昼贵索引, 夜贵索引]
 */
export function guiRen(ganIdx) {
    const row = GUI_REN_BY_GAN[((ganIdx % 10) + 10) % 10];
    if (!row)
        throw new RangeError(`日干索引越界：${ganIdx}`);
    return row;
}
/** 占时是否为昼（卯辰巳午未申为昼，酉戌亥子丑寅为夜） */
export function isDayTime(zhiIdx) {
    const i = ((zhiIdx % 12) + 12) % 12;
    return i >= 3 && i <= 8; // 卯(3) … 申(8)
}
// ════════════════════════════════════════════════════════════
// 六、刑 / 冲 / 合 / 害 / 破
// ════════════════════════════════════════════════════════════
/** 地支相冲（对宫） */
export function chong(zhiIdx) {
    return (zhiIdx + 6) % 12;
}
/**
 * 地支三刑。
 * 寅刑巳、巳刑申、申刑寅（无恩之刑）；
 * 丑刑戌、戌刑未、未刑丑（恃势之刑）；
 * 子刑卯、卯刑子（无礼之刑）；
 * 辰午酉亥自刑。
 */
const ZHI_XING = [3, 10, 5, 0, 4, 8, 6, 1, 2, 9, 7, 11];
/** 取地支之刑（辰午酉亥为自刑，返回其自身） */
export function xing(zhiIdx) {
    return ZHI_XING[((zhiIdx % 12) + 12) % 12];
}
/** 是否自刑（辰午酉亥） */
export function isSelfXing(zhiIdx) {
    const i = ((zhiIdx % 12) + 12) % 12;
    return i === 4 || i === 6 || i === 9 || i === 11;
}
/** 地支六合（子丑、寅亥、卯戌、辰酉、巳申、午未） */
export function liuHe(zhiIdx) {
    return (13 - (zhiIdx % 12)) % 12;
}
/** 地支六害（子未、丑午、寅巳、卯辰、申亥、酉戌） */
const ZHI_HAI = [7, 6, 5, 4, 3, 2, 1, 0, 11, 10, 9, 8];
/** 取地支所害 */
export function hai(zhiIdx) {
    return ZHI_HAI[((zhiIdx % 12) + 12) % 12];
}
/** 地支六破（子酉、丑辰、寅亥、卯午、巳申、未戌） */
const ZHI_PO = [9, 4, 11, 6, 1, 8, 3, 10, 5, 0, 7, 2];
/** 取地支所破 */
export function po(zhiIdx) {
    return ZHI_PO[((zhiIdx % 12) + 12) % 12];
}
/** 三合局：0=水局(申子辰) 1=木局(亥卯未) 2=火局(寅午戌) 3=金局(巳酉丑)，-1=非法 */
export function sanHeJu(zhiIdx) {
    const i = ((zhiIdx % 12) + 12) % 12;
    if (i % 4 === 0)
        return 0; // 子辰申
    if (i % 4 === 3)
        return 1; // 亥卯未
    if (i % 4 === 2)
        return 2; // 寅午戌
    return 3; // 巳酉丑（i % 4 === 1）
}
/** 取日干五合（甲己、乙庚、丙辛、丁壬、戊癸） */
export function ganHe(ganIdx) {
    return (ganIdx + 5) % 10;
}
// ════════════════════════════════════════════════════════════
// 七、旬空 / 遁干
// ════════════════════════════════════════════════════════════
/**
 * 由日柱（干支索引）取旬首地支索引。
 * 旬首即「甲X」，其地支索引 = (zhi - gan + 12) % 12，必为偶数（子/寅/辰/午/申/戌）。
 */
export function xunShouZhi(ganIdx, zhiIdx) {
    return (((zhiIdx - ganIdx) % 12) + 12) % 12;
}
/**
 * 旬空二支索引（旬首后第 10、11 位）。
 * @returns [空亡支索引1, 空亡支索引2]
 */
export function xunKong(ganIdx, zhiIdx) {
    const head = xunShouZhi(ganIdx, zhiIdx);
    return [(head + 10) % 12, (head + 11) % 12];
}
/** 旬名（甲子旬、甲戌旬…） */
export function xunName(ganIdx, zhiIdx) {
    return `甲${zhiOf(xunShouZhi(ganIdx, zhiIdx))}旬`;
}
/**
 * 旬遁天干：某地支在本旬中所遁之干。
 * 若该支落在旬空则返回 null（本旬无干可遁）。
 */
export function dunGan(ganIdx, zhiIdx, targetZhiIdx) {
    const head = xunShouZhi(ganIdx, zhiIdx);
    const offset = (((targetZhiIdx - head) % 12) + 12) % 12;
    if (offset >= 10)
        return null; // 空亡之支，无遁干
    return ganOf(offset);
}
// ════════════════════════════════════════════════════════════
// 八、长生十二宫
// ════════════════════════════════════════════════════════════
/** 长生十二宫名（阳干顺行次序） */
export const CHANG_SHENG_NAMES = [
    '长生',
    '沐浴',
    '冠带',
    '临官',
    '帝旺',
    '衰',
    '病',
    '死',
    '墓',
    '绝',
    '胎',
    '养',
];
/** 五阳干长生宫位（甲亥、丙寅、戊寅、庚巳、壬申）；阴干长生见下（逆行） */
const YANG_CHANG_SHENG = [11, 2, 2, 5, 8];
/** 五阴干长生宫位（乙午、丁酉、己酉、辛子、癸卯） */
const YIN_CHANG_SHENG = [6, 9, 9, 0, 3];
/**
 * 取某干在十二宫中的长生十二宫位名。
 * 阳干自长生宫顺行，阴干自长生宫逆行。
 */
export function changSheng(ganIdx, zhiIdx) {
    const g = ((ganIdx % 10) + 10) % 10;
    const z = ((zhiIdx % 12) + 12) % 12;
    if (isYangGan(g)) {
        const base = YANG_CHANG_SHENG[Math.floor(g / 2)];
        return CHANG_SHENG_NAMES[((z - base) % 12 + 12) % 12];
    }
    const base = YIN_CHANG_SHENG[Math.floor(g / 2)];
    return CHANG_SHENG_NAMES[((base - z) % 12 + 12) % 12];
}
/** 取某干的长生宫地支索引 */
export function changShengGong(ganIdx) {
    const g = ((ganIdx % 10) + 10) % 10;
    return isYangGan(g) ? YANG_CHANG_SHENG[Math.floor(g / 2)] : YIN_CHANG_SHENG[Math.floor(g / 2)];
}
/** 以「我」（日干五行索引）论某五行的六亲 */
export function liuQinOf(me, other) {
    if (me === other)
        return '兄弟';
    if (sheng(other, me))
        return '父母'; // 生我者为父母
    if (sheng(me, other))
        return '子孙'; // 我生者为子孙
    if (ke(other, me))
        return '官鬼'; // 克我者为官鬼
    return '妻财'; // 我克者为妻财
}
/** 六亲吉凶倾向（供断课使用） */
export const LIU_QIN_LUCK = {
    父母: 1,
    兄弟: 0,
    子孙: 1,
    妻财: 1,
    官鬼: -2,
};
// ════════════════════════════════════════════════════════════
// 十、神煞取法常量
// ════════════════════════════════════════════════════════════
/** 三合局 → 驿马 / 桃花 / 华盖 / 将星 / 劫煞 / 灾煞 取值（按同一三合局索引） */
export const SAN_HE_SHEN_SHA = [
    { ju: '申子辰（水局）', group: [8, 0, 4], yiMa: 2, taoHua: 9, huaGai: 4, jiangXing: 0, jieSha: 5, zaiSha: 6 },
    { ju: '亥卯未（木局）', group: [11, 3, 7], yiMa: 5, taoHua: 0, huaGai: 7, jiangXing: 3, jieSha: 8, zaiSha: 9 },
    { ju: '寅午戌（火局）', group: [2, 6, 10], yiMa: 8, taoHua: 3, huaGai: 10, jiangXing: 6, jieSha: 11, zaiSha: 0 },
    { ju: '巳酉丑（金局）', group: [5, 9, 1], yiMa: 11, taoHua: 6, huaGai: 1, jiangXing: 9, jieSha: 2, zaiSha: 3 },
];
/** 羊刃（禄前一位）：甲卯、乙辰、丙午、丁未、戊午、己未、庚酉、辛戌、壬子、癸丑 */
export const YANG_REN = [3, 4, 6, 7, 6, 7, 9, 10, 0, 1];
/** 飞刃（羊刃之冲） */
export function feiRen(ganIdx) {
    return chong(YANG_REN[((ganIdx % 10) + 10) % 10]);
}
/** 天德（按月支取） */
export const TIAN_DE = [
    '巳', '庚', '丁', '申', '壬', '辛', '亥', '甲', '癸', '寅', '丙', '乙',
];
/** 月德（按 sanHeJu 局索引：水局壬、木局甲、火局丙、金局庚） */
export const YUE_DE = ['壬', '甲', '丙', '庚'];
/** 破碎（按日支取：四仲日→巳、四季日→丑、四孟日→酉） */
const PO_SUI = [5, 1, 9, 5, 1, 9, 5, 1, 9, 5, 1, 9];
/** 取破碎煞 */
export function poSui(zhiIdx) {
    return PO_SUI[((zhiIdx % 12) + 12) % 12];
}
/** 天喜（按季取：春戌、夏丑、秋辰、冬未；月支所属季） */
const TIAN_XI = [7, 7, 10, 10, 10, 1, 1, 1, 4, 4, 4, 7];
/** 取天喜（入参为月支索引） */
export function tianXi(yueZhiIdx) {
    return TIAN_XI[((yueZhiIdx % 12) + 12) % 12];
}
/** 天马（寅月起午，逐月进二位，只行六阳位；索引 = 月支索引） */
const TIAN_MA = [2, 4, 6, 8, 10, 0, 2, 4, 6, 8, 10, 0];
/** 取天马（入参为月支索引） */
export function tianMa(yueZhiIdx) {
    return TIAN_MA[((yueZhiIdx % 12) + 12) % 12];
}
/** 红鸾（子年起卯逆行） */
export function hongLuan(nianZhiIdx) {
    const i = ((nianZhiIdx % 12) + 12) % 12;
    return ((3 - i) % 12 + 12) % 12;
}
/** 天哭（子年起午逆行） */
export function tianKu(nianZhiIdx) {
    const i = ((nianZhiIdx % 12) + 12) % 12;
    return ((6 - i) % 12 + 12) % 12;
}
/** 病符（旧岁太岁，年支前一位） */
export function bingFu(nianZhiIdx) {
    return ((nianZhiIdx - 1) % 12 + 12) % 12;
}
/** 死符（病符之冲） */
export function siFu(nianZhiIdx) {
    return chong(bingFu(nianZhiIdx));
}
/** 孤辰 / 寡宿（按年支） */
export const GU_GUA = [
    { group: [11, 0, 1], gu: 2, gua: 10 }, // 亥子丑年 → 孤寅 寡戌
    { group: [2, 3, 4], gu: 5, gua: 1 }, // 寅卯辰年 → 孤巳 寡丑
    { group: [5, 6, 7], gu: 8, gua: 4 }, // 巳午未年 → 孤申 寡辰
    { group: [8, 9, 10], gu: 11, gua: 7 }, // 申酉戌年 → 孤亥 寡未
];
/** 取孤辰寡宿 */
export function guGua(nianZhiIdx) {
    const i = ((nianZhiIdx % 12) + 12) % 12;
    const row = GU_GUA.find((r) => r.group.includes(i));
    return row ? { gu: row.gu, gua: row.gua } : { gu: -1, gua: -1 };
}
// ════════════════════════════════════════════════════════════
// 十一、四孟 / 四仲 / 四季
// ════════════════════════════════════════════════════════════
/** 四孟（寅申巳亥） */
export const SI_MENG = [2, 5, 8, 11];
/** 四仲（子午卯酉） */
export const SI_ZHONG = [0, 3, 6, 9];
/** 四季（辰戌丑未） */
export const SI_JI = [1, 4, 7, 10];
/** 孟仲季分类：'孟' | '仲' | '季' */
export function mengZhongJi(zhiIdx) {
    const i = ((zhiIdx % 12) + 12) % 12;
    if (SI_MENG.includes(i))
        return '孟';
    if (SI_ZHONG.includes(i))
        return '仲';
    return '季';
}
/** 地支 → 生肖（可用于文案） */
export const SHENG_XIAO = [
    '鼠', '牛', '虎', '兔', '龙', '蛇', '马', '羊', '猴', '鸡', '狗', '猪',
];
//# sourceMappingURL=constants.js.map